import { useState } from 'react'
import { Camera, Pencil } from 'lucide-react'
import { Avatar, AvatarFallback, AvatarImage } from '@teach/ui'
import { AuthorBadges } from '@/components/common/AuthorBadges'
import { useAuthorStatus } from '@/features/auth/useAuthorStatus'
import { useMe } from '@/features/auth/useMe'
import { useGetIdProfileQuery } from '../profileApi'
import { ChangeAvatarDialog } from './ChangeAvatarDialog'
import { ChangeEmailDialog } from './ChangeEmailDialog'
import { ChangeNicknameDialog } from './ChangeNicknameDialog'
import { useMessages } from '@/i18n'
import { messages } from './messages'

type Editing = 'avatar' | 'nickname' | 'email' | null

const initialsOf = (name: string) =>
  name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()

/** Кто мы: ник (как в академии: сначала из ID-сервиса, потом username), почта, инициалы. */
const useIdentity = () => {
  const { data: me } = useMe()
  const { data: idProfile } = useGetIdProfileQuery()
  const nickname = idProfile?.profileUsername || me?.username || ''
  const name = nickname || me?.email?.split('@')[0] || 'User'
  return {
    nickname,
    name,
    initials: initialsOf(name),
    email: idProfile?.email ?? me?.email ?? null,
    avatar: me?.avatar ?? null,
    userId: idProfile?.id,
    canEdit: Boolean(idProfile),
  }
}

interface TileProps {
  avatar: string | null
  name: string
  initials: string
  canEdit: boolean
  onEdit: () => void
}

const AvatarTile = ({ avatar, name, initials, canEdit, onEdit }: TileProps) => {
  const m = useMessages(messages)
  const picture = (
    <Avatar className="size-16 rounded-xl">
      {avatar ? <AvatarImage src={avatar} alt={name} /> : null}
      <AvatarFallback className="rounded-xl text-lg">{initials}</AvatarFallback>
    </Avatar>
  )
  if (!canEdit) return <div className="shrink-0">{picture}</div>
  return (
    <button
      type="button"
      aria-label={m.changeAvatar}
      onClick={onEdit}
      className="group relative shrink-0 rounded-xl outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      {picture}
      <span className="absolute inset-0 flex items-center justify-center rounded-xl bg-black/50 opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
        <Camera className="size-5 text-white" />
      </span>
    </button>
  )
}

/** Шапка профиля: аватар, ник, почта. Правка (аватарка, ник, почта) идёт через
 *  ID-сервис: аккаунт общий со студией и академией. Кнопки правки видны, только
 *  когда ID-сессия есть и профиль загрузился. */
export const ProfileHeader = () => {
  const { nickname, name, initials, email, avatar, userId, canEdit } = useIdentity()
  const { isAuthor, isVerified } = useAuthorStatus()
  const m = useMessages(messages)
  const [editing, setEditing] = useState<Editing>(null)
  const dialog = (kind: Exclude<Editing, null>) => ({
    open: editing === kind,
    onOpenChange: (open: boolean) => setEditing(open ? kind : null),
  })

  return (
    <div className="flex items-center gap-4">
      <AvatarTile
        avatar={avatar}
        name={name}
        initials={initials}
        canEdit={canEdit}
        onEdit={() => setEditing('avatar')}
      />
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <AuthorBadges beta={isAuthor} verified={isVerified} size="lg" />
          <h2 className="truncate text-2xl font-semibold">{name}</h2>
          {canEdit ? (
            <button
              type="button"
              aria-label={m.changeNickname}
              onClick={() => setEditing('nickname')}
              className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-foreground/10 hover:text-foreground"
            >
              <Pencil className="size-4" />
            </button>
          ) : null}
        </div>
        <div className="flex items-center gap-2 text-muted-foreground">
          <span className="truncate">{email ?? m.noEmail}</span>
          {canEdit ? (
            <button
              type="button"
              onClick={() => setEditing('email')}
              className="text-sm underline underline-offset-4 hover:text-foreground"
            >
              {m.change}
            </button>
          ) : null}
        </div>
      </div>

      <ChangeAvatarDialog {...dialog('avatar')} current={avatar} />
      <ChangeNicknameDialog {...dialog('nickname')} userId={userId} current={nickname} />
      <ChangeEmailDialog {...dialog('email')} current={email} />
    </div>
  )
}
